import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Player } from "../../types";
import { apiService } from "../../services/api";
import { dictionaries } from "../../utils/dictionary";
import {
  PhaseBackground,
  PhaseContainer,
  PhaseMain,
  PhaseButton,
  InstructionCard,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";
import PhaseHeader from "./PhaseHeader";

interface PlayerSetupProps {
  language: number;
  clubId?: number;
  streamLink?: string;
  frequentPlayers?: Player[];
  eventParticipants?: Player[];
  initialJudge?: Player; // Pre-set judge for tournament games
  onPlayersReady: (players: Player[], judge?: Player) => void;
  onPlayersChange?: (players: Player[], allReady: boolean) => void;
  onCancel: () => void;
  gameNumber: number;
  onGameNumberChange?: (gameNumber: number) => void;
  onStreamSettingsChange?: (theme: string, language: number) => void;
  currentUser?: {
    id: number;
    nickname: string;
    avatar: string;
  };
  // Timer state
  timerAccumulated?: number;
  timerStartedAt?: number;
  onTimerStateChange?: (accumulated: number, startedAt: number | undefined) => void;
  onTimeoutToggle?: () => void;
}

const InputGroup = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: ${theme.spacing.md};
  position: relative;
`;

const InputRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  width: 100%;

  @media (max-width: 768px) {
    gap: ${theme.spacing.sm};
  }
  
  & > ${InputGroup}:first-child {
    flex: 1 1 auto;
    min-width: 0;
    margin-bottom: 0;
  }
  
  & > ${InputGroup}:last-child {
    flex: 0 0 auto;
    width: 180px;
    margin-bottom: 0;
    
    @media (max-width: 768px) {
      width: 100px;
    }
  }
`;

const InputLabel = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: 0 ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md} 0 0 ${theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${theme.colors.border.secondary};
  border-right: none;
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  min-width: 60px;
  justify-content: center;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: all ${theme.transitions.normal};

  @media (max-width: 768px) {
    padding: 0 0.5rem;
    font-size: 0.8rem;
    min-width: 50px;
  }

  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.08)"};
  }
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 0.75rem ${theme.spacing.md};
  border-radius: 0;
  border: 1px solid ${theme.colors.border.secondary};
  border-left: none;
  border-right: none;
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  transition: all ${theme.transitions.normal};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const ClearButton = styled.button.attrs({ type: "button" })`
  padding: 0 ${theme.spacing.md};
  border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
  border: 1px solid ${theme.colors.border.secondary};
  border-left: none;
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.secondary};
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  @media (max-width: 768px) {
    padding: 0 0.5rem;
    font-size: 1.3rem;
  }

  &:hover {
    background: rgba(255, 77, 109, 0.25);
    color: ${theme.colors.text.primary};
  }
`;

const PlayerAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.border.secondary};
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: rgba(18, 12, 36, 0.98);
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  z-index: 1000;
`;

const SearchResultItem = styled.button.attrs({ type: "button" })`
  width: 100%;
  padding: ${theme.spacing.md};
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: ${theme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  &:hover {
    background: rgba(124, 93, 255, 0.15);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.border.secondary};
`;

const SearchResultInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SearchResultName = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
`;

const SearchResultMeta = styled.span`
  font-size: 0.85rem;
  color: ${theme.colors.text.secondary};
`;

const FrequentPlayersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 768px) {
    gap: 0.4rem;
    margin-bottom: ${theme.spacing.md};
    max-height: 120px;
    overflow-y: auto;
  }
`;

const FrequentPlayerButton = styled(PhaseButton)`
  font-size: 0.8rem;
  padding: 0.4rem 0.75rem;
  border-radius: ${theme.borderRadius.sm};

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.35rem 0.6rem;
    border-radius: 8px;
  }
`;

const InfoBox = styled(InstructionCard)`
  margin-top: ${theme.spacing.xl};

  b {
    display: block;
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.text.primary};
  }
`;

const InfoContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
`;

const InfoText = styled.span`
  /* flex: 1; */
  font-size: 0.9rem;
  word-break: break-all;
  color: ${theme.colors.text.primary};
`;

const CopyButton = styled.button.attrs({ type: "button" })`
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.08);
  color: ${theme.colors.text.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  white-space: nowrap;

  &:hover {
    background: rgba(124, 93, 255, 0.25);
    border-color: ${theme.colors.border.strong};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const SelectGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const SelectLabel = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${theme.colors.text.primary};
`;

const StyledSelect = styled.select`
  padding: 0.75rem ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  option {
    background: rgba(18, 12, 36, 0.98);
    color: ${theme.colors.text.primary};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${theme.spacing.md};
`;

const ModalCard = styled.div`
  background: rgba(18, 12, 36, 0.98);
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  box-shadow: ${theme.shadows.xl};
`;

const ModalTitle = styled.h3`
  margin: 0 0 ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
  font-size: 1.25rem;
  font-weight: 700;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.75rem ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  font-size: 1rem;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
  }

  &::selection {
    background: rgba(124, 93, 255, 0.5);
    color: ${theme.colors.text.primary};
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
`;

const STREAM_THEMES = [
  { value: 'classic', label: 'theme_classic' },
  { value: 'dark', label: 'theme_dark' },
  { value: 'noroles', label: 'theme_noroles' },
  { value: 'cartel', label: 'theme_cartel' },
  { value: 'x-city', label: 'theme_xcity' },
];

const STREAM_LANGUAGES = [
  { value: 0, label: 'lang_english' },
  { value: 1, label: 'lang_russian' },
  { value: 2, label: 'lang_ukrainian' },
  { value: 3, label: 'lang_georgian' },
  { value: 4, label: 'lang_hebrew' },
];

const PlayerSetup: React.FC<PlayerSetupProps> = ({
  language,
  clubId,
  streamLink,
  frequentPlayers = [],
  eventParticipants = [],
  initialJudge,
  onPlayersReady,
  onPlayersChange,
  onCancel,
  gameNumber,
  onGameNumberChange,
  onStreamSettingsChange,
  currentUser,
  timerAccumulated,
  timerStartedAt,
  onTimerStateChange,
  onTimeoutToggle,
}) => {
  // Combine event participants and frequent players, prioritizing participants
  const getSuggestedPlayers = (): Player[] => {
    const suggested: Player[] = [];
    const addedIds = new Set<number>();
    const addedNicknames = new Set<string>(); // Track guests by nickname
    let nextGuestId = -11; // Start guest IDs from -11 to avoid conflict with slot guests (-1 to -10)

    // First, add event participants
    eventParticipants.forEach((player) => {
      // Handle registered players (positive user_id)
      if (player.user_id > 0 && !addedIds.has(player.user_id)) {
        suggested.push(player);
        addedIds.add(player.user_id);
      }
      // Handle guests/participants without user_id (user_id <= 0 or undefined)
      else if ((!player.user_id || player.user_id <= 0) && player.nickname && !addedNicknames.has(player.nickname)) {
        // Assign a temporary negative ID if not already set
        const guestPlayer: Player = {
          ...player,
          user_id: player.user_id && player.user_id < 0 ? player.user_id : nextGuestId--,
        };
        suggested.push(guestPlayer);
        addedNicknames.add(player.nickname);
      }
    });

    // Then, add frequent players who aren't already added (only registered users)
    frequentPlayers.forEach((player) => {
      if (player.user_id > 0 && !addedIds.has(player.user_id)) {
        suggested.push(player);
        addedIds.add(player.user_id);
      }
    });

    return suggested;
  };
  const [players, setPlayers] = useState<(Player | null)[]>(
    Array(10).fill(null)
  );
  const [judge, setJudge] = useState<Player | null>(null);
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [isJudgeSlot, setIsJudgeSlot] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestSlotIndex, setGuestSlotIndex] = useState<number | null>(null);
  const [guestNickname, setGuestNickname] = useState("");
  const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const guestInputRef = React.useRef<HTMLInputElement | null>(null);
  
  // Stream settings state - load from localStorage or use defaults
  const [streamTheme, setStreamTheme] = useState<string>(() => {
    return localStorage.getItem('streamTheme') || 'dark';
  });
  const [streamLanguage, setStreamLanguage] = useState<number>(() => {
    const saved = localStorage.getItem('streamLanguage');
    return saved !== null ? parseInt(saved) : 1; // Default to Russian (1)
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle stream settings changes
  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('streamTheme', streamTheme);
    localStorage.setItem('streamLanguage', streamLanguage.toString());
    
    // Notify parent component
    if (onStreamSettingsChange) {
      onStreamSettingsChange(streamTheme, streamLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamTheme, streamLanguage]); // Intentionally omitting onStreamSettingsChange to avoid infinite loop

  // Auto-fill players from tournament protocol (when eventParticipants has exactly 10 players)
  useEffect(() => {
    if (!clubId && eventParticipants.length === 10) {
      // This is a tournament game with pre-seated players
      setPlayers(eventParticipants);
    }
  }, [clubId, eventParticipants]);

  // Set judge from initialJudge (for tournament games) or current user (for club games)
  useEffect(() => {
    if (initialJudge && !judge) {
      // Tournament game - use provided judge
      setJudge(initialJudge);
    } else if (clubId && currentUser && !judge) {
      // Club game - use current user as default judge
      const defaultJudge: Player = {
        user_id: currentUser.id,
        nickname: currentUser.nickname,
        avatar: currentUser.avatar,
        games_played_red: 0,
        games_played_black: 0,
        games_played_sheriff: 0,
        games_played_don: 0,
        wins_red: 0,
        wins_black: 0,
        wins_sheriff: 0,
        wins_don: 0,
        c_games_played_red: 0,
        c_games_played_black: 0,
        c_games_played_sheriff: 0,
        c_games_played_don: 0,
        c_wins_red: 0,
        c_wins_black: 0,
        c_wins_sheriff: 0,
        c_wins_don: 0,
      };
      setJudge(defaultJudge);
    }
  }, [clubId, currentUser, judge, initialJudge]);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Auto-select text when guest modal opens
  useEffect(() => {
    if (showGuestModal && guestInputRef.current) {
      // Use setTimeout to ensure the input is fully mounted and focused
      setTimeout(() => {
        guestInputRef.current?.select();
      }, 0);
    }
  }, [showGuestModal]);

  const dict = dictionaries[language].ph;

  useEffect(() => {
    const searchPlayers = async (term: string) => {
      try {
        const response = await apiService.searchUsers(term, clubId);
        if (response.success) {
          setSearchResults(response.success);
        }
      } catch (error) {
        console.error("Search failed:", error);
      }
    };

    if (searchTerm.length >= 1) {
      searchPlayers(searchTerm);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, clubId]);

  const handlePlayerSelect = (player: Player) => {
    if (activeSlot === null) return;

    // Check for duplicates
    if (player.user_id > 0) {
      // For registered players, check by user_id
      const isDuplicate =
        players.some((p) => p?.user_id != null && p.user_id > 0 && p.user_id === player.user_id) ||
        (judge?.user_id != null && judge.user_id > 0 && judge.user_id === player.user_id);

      if (isDuplicate) {
        alert(`${dict.player} ${player.nickname} ${dict.isdup}`);
        return;
      }
    } else {
      // For guests (negative or no user_id), check by nickname
      const isDuplicate =
        players.some((p) => p?.user_id != null && p.user_id <= 0 && p.nickname === player.nickname) ||
        (judge?.user_id != null && judge.user_id <= 0 && judge.nickname === player.nickname);

      if (isDuplicate) {
        alert(`${dict.player} ${player.nickname} ${dict.isdup}`);
        return;
      }
    }

    if (isJudgeSlot) {
      setJudge(player);
    } else {
      const newPlayers = [...players];
      newPlayers[activeSlot] = player;
      setPlayers(newPlayers);
    }

    setSearchTerm("");
    setActiveSlot(null);
    setIsJudgeSlot(false);
  };

  const handleSlotClick = (index: number) => {
    // Cancel any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    // If slot is already active, don't reset
    if (activeSlot === index && !isJudgeSlot) {
      return;
    }
    
    setActiveSlot(index);
    setIsJudgeSlot(false);
    setSearchTerm("");
  };

  const handleJudgeSlotClick = () => {
    // Cancel any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    // If judge slot is already active, don't reset
    if (isJudgeSlot && activeSlot === -1) {
      return;
    }
    
    setActiveSlot(-1);
    setIsJudgeSlot(true);
    setSearchTerm("");
  };

  const handleBlur = () => {
    // Clear search term and active slot when focus is lost
    blurTimeoutRef.current = setTimeout(() => {
      setSearchTerm("");
      setActiveSlot(null);
      setIsJudgeSlot(false);
      blurTimeoutRef.current = null;
    }, 200); // Small delay to allow click on search results
  };

  const clearSlot = (index: number) => {
    const newPlayers = [...players];
    newPlayers[index] = null;
    setPlayers(newPlayers);
  };

  const clearJudge = () => {
    setJudge(null);
  };

  const isPlayerAdded = (player: Player): boolean => {
    // For registered players (positive user_id), check by ID
    if (player.user_id > 0) {
      return (
        players.some((p) => p?.user_id != null && p.user_id > 0 && p.user_id === player.user_id) ||
        (judge?.user_id != null && judge.user_id > 0 && judge.user_id === player.user_id)
      );
    }
    // For guests (negative or no user_id), check by nickname
    return (
      players.some((p) => p?.user_id != null && p.user_id <= 0 && p.nickname === player.nickname) ||
      (judge?.user_id != null && judge.user_id <= 0 && judge.nickname === player.nickname)
    );
  };

  const addFrequentPlayer = (player: Player) => {
    // Check for duplicates
    if (isPlayerAdded(player)) {
      alert(`${dict.player} ${player.nickname} ${dict.isdup}`);
      return;
    }

    const emptySlot = players.findIndex((p) => p === null);
    if (emptySlot !== -1) {
      const newPlayers = [...players];
      newPlayers[emptySlot] = player;
      setPlayers(newPlayers);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const createGuestPlayer = (index: number, guestName: string) => {
    const guestPlayer: Player = {
      user_id: -(index + 1), // Use negative IDs for guests: -1, -2, -3, ..., -10
      nickname: guestName,
      avatar: "https://app.mafiaworldtour.org/img/alive.png",
      games_played_red: 0,
      games_played_black: 0,
      games_played_sheriff: 0,
      games_played_don: 0,
      wins_red: 0,
      wins_black: 0,
      wins_sheriff: 0,
      wins_don: 0,
      c_games_played_red: 0,
      c_games_played_black: 0,
      c_games_played_sheriff: 0,
      c_games_played_don: 0,
      c_wins_red: 0,
      c_wins_black: 0,
      c_wins_sheriff: 0,
      c_wins_don: 0,
    };

    const newPlayers = [...players];
    newPlayers[index] = guestPlayer;
    setPlayers(newPlayers);
  };

  const openGuestModal = (index: number) => {
    const defaultGuestName = dict.guest_player_label.replace(
      "{0}",
      String(index + 1)
    );
    
    setGuestSlotIndex(index);
    setGuestNickname(defaultGuestName);
    setShowGuestModal(true);
  };

  const closeGuestModal = () => {
    setShowGuestModal(false);
    setGuestSlotIndex(null);
    setGuestNickname("");
  };

  const confirmGuestPlayer = () => {
    if (guestSlotIndex !== null && guestNickname.trim()) {
      createGuestPlayer(guestSlotIndex, guestNickname.trim());
      closeGuestModal();
    }
  };

  const isReady = () => {
    const allPlayersFilled = players.every((p) => p !== null);
    const judgeReady = judge !== null; // Judge is always required
    return allPlayersFilled && judgeReady;
  };

  // Notify parent when players change
  useEffect(() => {
    if (onPlayersChange) {
      const filledPlayers = players.filter((p) => p !== null) as Player[];
      const allPlayersFilled = players.every((p) => p !== null);
      const judgeReady = judge !== null; // Judge is always required
      const ready = allPlayersFilled && judgeReady;
      onPlayersChange(filledPlayers, ready);
    }
  }, [players, judge, clubId, onPlayersChange]);

  const handleNext = () => {
    if (isReady()) {
      onPlayersReady(
        players.filter((p) => p !== null) as Player[],
        judge || undefined
      );
    }
  };

  return (
    <PhaseBackground $variant="setup">
      <PhaseContainer>
        <PhaseHeader
          title={dict.adding_players}
          subtitle={`${players.filter((p) => p !== null).length}/10`}
          variant="day"
          language={language}
          onTimeoutToggle={onTimeoutToggle || (() => {})}
          timerAccumulated={timerAccumulated}
          timerStartedAt={timerStartedAt}
          onTimerStateChange={onTimerStateChange}
          actions={[
            {
              id: "next",
              label: dict.nxt,
              onClick: handleNext,
              disabled: !isReady(),
              variant: "primary",
            },
            {
              id: "cancel",
              label: dict.cncl,
              onClick: onCancel,
              variant: "secondary",
            },
          ]}
        />

        <PhaseMain>
          {/* Judge and Game number on one row */}
          <InputRow>
            <InputGroup>
              <InputLabel>
                {dict.j}
                {judge && (
                  <PlayerAvatar
                    src={judge.avatar}
                    alt={dict.judge_avatar_alt}
                  />
                )}
                {judge?.level && (
                  <span style={{ marginLeft: "8px", color: "#7c5dff" }}>
                    {dict.level_short.replace("{0}", String(judge.level))}
                  </span>
                )}
              </InputLabel>
              <StyledInput
                type="text"
                value={isJudgeSlot ? searchTerm : judge?.nickname || ""}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleJudgeSlotClick}
                onBlur={handleBlur}
                placeholder={judge ? "" : dict.srch + "..."}
              />
              <ClearButton onClick={clearJudge}>×</ClearButton>

              {/* Search results for judge slot */}
              {searchResults.length > 0 && isJudgeSlot && (
                <SearchDropdown>
                  {searchResults.slice(0, 20).map((player) => (
                    <SearchResultItem
                      key={player.user_id}
                      onClick={() => handlePlayerSelect(player)}
                    >
                      <SearchResultAvatar
                        src={player.avatar}
                        alt={dict.player_avatar_alt.replace(
                          "{0}",
                          player.nickname
                        )}
                      />
                      <SearchResultInfo>
                        <SearchResultName>{player.nickname}</SearchResultName>
                        {player.club_name && (
                          <SearchResultMeta>
                            {player.club_name}
                          </SearchResultMeta>
                        )}
                      </SearchResultInfo>
                    </SearchResultItem>
                  ))}
                </SearchDropdown>
              )}
            </InputGroup>

            <InputGroup>
              <InputLabel>{dict.gamecaps} #</InputLabel>
              <StyledInput
                type="number"
                value={gameNumber}
                readOnly={!clubId}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value > 0 && onGameNumberChange) {
                    onGameNumberChange(value);
                  }
                }}
                min="1"
                style={{
                  borderRadius: `0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0`,
                }}
              />
            </InputGroup>
          </InputRow>

          {/* Suggested players for club games (event participants + frequent players) */}
          {clubId && (eventParticipants.length > 0 || frequentPlayers.length > 0) && (
            <FrequentPlayersGrid>
              {getSuggestedPlayers()
                .filter((player) => !isPlayerAdded(player))
                .slice(0, 20)
                .map((player) => (
                  <FrequentPlayerButton
                    key={`${player.user_id}-${player.nickname}`}
                    $variant="outline"
                    onClick={() => addFrequentPlayer(player)}
                  >
                    {player.nickname}
                  </FrequentPlayerButton>
                ))}
            </FrequentPlayersGrid>
          )}

          {/* Player slots */}
          {players.map((player, index) => (
            <InputGroup key={index}>
              <InputLabel
                $clickable={!player && !!clubId}
                onClick={() => !player && clubId && openGuestModal(index)}
                title={!player && clubId ? dict.add_guest : ""}
              >
                {index + 1 < 10 ? `0${index + 1}` : index + 1}
                {player && (
                  <PlayerAvatar
                    src={player.avatar}
                    alt={dict.player_avatar_alt.replace(
                      "{0}",
                      player.nickname
                    )}
                  />
                )}
              </InputLabel>
              <StyledInput
                type="text"
                value={
                  activeSlot === index ? searchTerm : player?.nickname || ""
                }
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => handleSlotClick(index)}
                onBlur={handleBlur}
                placeholder={player ? "" : dict.srch + "..."}
              />
              <ClearButton onClick={() => clearSlot(index)}>×</ClearButton>

              {/* Search results for this player slot */}
              {searchResults.length > 0 && activeSlot === index && (
                <SearchDropdown>
                  {searchResults.slice(0, 20).map((searchPlayer) => (
                    <SearchResultItem
                      key={searchPlayer.user_id}
                      onClick={() => handlePlayerSelect(searchPlayer)}
                    >
                      <SearchResultAvatar
                        src={searchPlayer.avatar}
                        alt={dict.player_avatar_alt.replace(
                          "{0}",
                          searchPlayer.nickname
                        )}
                      />
                      <SearchResultInfo>
                        <SearchResultName>
                          {searchPlayer.nickname}
                        </SearchResultName>
                        {searchPlayer.club_name && (
                          <SearchResultMeta>
                            {searchPlayer.club_name}
                          </SearchResultMeta>
                        )}
                      </SearchResultInfo>
                    </SearchResultItem>
                  ))}
                </SearchDropdown>
              )}
            </InputGroup>
          ))}

          {/* Stream links */}
          <InfoBox>
            <b>{dict.stream_settings}</b>
            
            <SelectWrapper>
              <SelectGroup>
                <SelectLabel htmlFor="stream-theme">{dict.stream_theme_label}</SelectLabel>
                <StyledSelect
                  id="stream-theme"
                  value={streamTheme}
                  onChange={(e) => setStreamTheme(e.target.value)}
                >
                  {STREAM_THEMES.map((theme) => (
                    <option key={theme.value} value={theme.value}>
                      {dict[theme.label]}
                    </option>
                  ))}
                </StyledSelect>
              </SelectGroup>
              
              <SelectGroup>
                <SelectLabel htmlFor="stream-language">{dict.stream_language_label}</SelectLabel>
                <StyledSelect
                  id="stream-language"
                  value={streamLanguage}
                  onChange={(e) => setStreamLanguage(parseInt(e.target.value))}
                >
                  {STREAM_LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {dict[lang.label]}
                    </option>
                  ))}
                </StyledSelect>
              </SelectGroup>
            </SelectWrapper>

            {streamLink && (
              <InfoContent>
                <InfoText>{streamLink}</InfoText>
                <CopyButton onClick={() => copyToClipboard(streamLink)}>
                  📋 {dict.copylink || "Copy"}
                </CopyButton>
              </InfoContent>
            )}
          </InfoBox>

          {clubId && (
            <InfoBox>
              <b>{dict.registration}</b>
              <InfoContent>
                <InfoText>
                  https://mafiaworldtour.org/register?club={clubId}
                </InfoText>
                <CopyButton
                  onClick={() =>
                    copyToClipboard(
                      `https://mafiaworldtour.org/register?club=${clubId}`
                    )
                  }
                >
                  📋 {dict.copylink || "Copy"}
                </CopyButton>
              </InfoContent>
            </InfoBox>
          )}
        </PhaseMain>
      </PhaseContainer>

      {/* Guest Player Modal */}
      {showGuestModal && (
        <ModalOverlay onMouseDown={(e) => {
          // Only close if click started on the overlay itself (not on modal content)
          if (e.target === e.currentTarget) {
            closeGuestModal();
          }
        }}>
          <ModalCard>
            <ModalTitle>
              {dict.add_guest} ({guestSlotIndex !== null ? guestSlotIndex + 1 : ''})
            </ModalTitle>
            <ModalInput
              ref={guestInputRef}
              type="text"
              value={guestNickname}
              onChange={(e) => setGuestNickname(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && guestNickname.trim()) {
                  e.preventDefault();
                  confirmGuestPlayer();
                }
              }}
              placeholder={dict.enter_nickname || "Enter nickname"}
              autoFocus
            />
            <ModalButtonGroup>
              <PhaseButton $variant="secondary" onClick={closeGuestModal}>
                {dict.cncl}
              </PhaseButton>
              <PhaseButton
                $variant="primary"
                onClick={confirmGuestPlayer}
                disabled={!guestNickname.trim()}
              >
                {dict.confirm}
              </PhaseButton>
            </ModalButtonGroup>
          </ModalCard>
        </ModalOverlay>
      )}
    </PhaseBackground>
  );
};

export default PlayerSetup;
